import { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Button, Spin, Flex, message, Typography, Popconfirm, Modal, Tag, Divider, Avatar, Radio, Tooltip, Table, Splitter, ConfigProvider } from 'antd';
import { DeleteOutlined, CloseCircleOutlined, FileImageOutlined, InfoCircleOutlined, CheckCircleFilled, ClockCircleOutlined, TableOutlined } from '@ant-design/icons';
import {
  useGetInspectionPlanByProjectQuery,
  useGetRelatedFilesQuery,
  useLinkKeyenceReportMutation,
  useOverrideKeyenceReportMutation,
  useDeleteKeyenceReportMutation,
  useUnlinkKeyenceReportMutation,
} from '../../../../redux/api/quality';
import { InspectionPlan, KeyenceReport, SampleViewItem } from '../../../../interfaces/Quality.Interface';
import { useGetUsersQuery, UserQueryInterface } from '../../../../redux/api/users';
import OperatorPinField from '../../../Operators/OperatorPinField';
import dayjs from 'dayjs';
import useControlsDisabled from '../../../../hooks/useControlsDisabled';
import { useGetBallooningProjectQuery } from '../../../../redux/api/ballooning';
import BallooningViewer from '../../../BallooningViewer';
// BallooningViewer has only a default export; `Balloon` lives in the interfaces
// module. Imported as a type so `isolatedModules` cannot emit a runtime import
// for an export that does not exist.
import type { Balloon } from '../../../../interfaces/Ballooning';
import TabletSelectModal, { TabletSelectOption } from '../../TabletSelectModal';
import KeyenceSampleReportResults, { KeyenceReportSource } from '../KeyenceSampleReportResults';
import {
  InspectionSampleLabelingTag,
  confirmSampleLabeling,
} from '../../../InspectionSampleLabeling';
import { THEME } from '../../../../main';

const { Title, Text } = Typography;

const trimZeros = (v: string | null | undefined): string => {
  if (!v) return '—';
  return String(v).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
};

const parseSampleValue = (value: string) => {
  const [sampleId, view, deviceName] = value.split('|');
  return { sampleId, view, deviceName };
};

const refreshSelectedSampleReport = (sampleValue: string, planData: any) => {
  const { sampleId, view, deviceName } = parseSampleValue(sampleValue);
  const matched = planData?.sample_view_list.find(
    (entry: any) =>
      entry.sample_id === sampleId &&
      entry.view === view &&
      (entry.device_name || null) === (deviceName || null)
  );
  const report = matched?.report || null;
  return { report, locked: !!report?.linked };
};

export type KeyenceCardProps = {
  plan: InspectionPlan;
  onUpdate?: () => void;
  passedSample?: SampleViewItem;
  lockSample?: boolean;
  isLiveQueue?: boolean;
  deviceName?: string;
  showResults?: boolean;
};

/** The Keyence linking UI without the card chrome, for hosts that frame it
 *  themselves (e.g. a modal). `framed` is set only by `KeyenceCard`. */
export const KeyenceCardContent = ({ plan, onUpdate, passedSample, lockSample, isLiveQueue = false, deviceName, showResults = false, framed = false }: KeyenceCardProps & { framed?: boolean }) => {
  const [internalSelected, setInternalSelected] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false);
  const [isReportLocked, setIsReportLocked] = useState(false);
  const [allLinked, setAllLinked] = useState(false);

  const selectedSample = internalSelected ?? passedSample?.value;
  const isSampleLocked = lockSample ?? false;
  const [isManualSelection, setIsManualSelection] = useState(false);
  const [operatorId, setOperatorId] = useState<number | undefined>(undefined);
  const [operatorError, setOperatorError] = useState(false);

  const userQuery: UserQueryInterface = useMemo(() => ({
    groups__name: 'Keyence Operators',
  }), []);

  const { data: usersData } = useGetUsersQuery(userQuery);
  const users = usersData?.results;

  const isUserMissing = !operatorId;

  // Drawing viewer state
  const [showDrawing, setShowDrawing] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [allKeyenceBalloons, setAllKeyenceBalloons] = useState<Balloon[]>([]);
  const [drawingViewFilter, setDrawingViewFilter] = useState<string>('all');
  const [activeBalloonId, setActiveBalloonId] = useState<string | null>(null);
  const [centerOnActive, setCenterOnActive] = useState(false);

  // The full ballooning project is only used by the drawing modal below. Load it
  // the first time the operator opens the drawing, not on mount: the Keyence
  // queue mounts ~100 cards at once on every return to the page, and these
  // requests were 78% of each reload's server time (PTB-6954). Latched, so
  // closing and reopening the drawing does not fetch it again.
  const [resultsReport, setResultsReport] = useState<KeyenceReport | null>(null);
  // Linked "Results" on the card reads the CSV copied at link time; list
  // "Results" always goes live through the proxy (the device may have rewritten
  // the file).
  const [resultsPreferLive, setResultsPreferLive] = useState(false);
  const [reportPickerOpen, setReportPickerOpen] = useState(false);
  const showResultsModal = resultsReport !== null;
  const openResults = (report: KeyenceReport, preferLive = false) => {
    // Close the CSV list first so Results is not buried under its higher nest z-index.
    setReportPickerOpen(false);
    setResultsPreferLive(preferLive);
    setResultsReport(report);
  };
  const closeResults = () => {
    setResultsReport(null);
    setResultsPreferLive(false);
  };
  const [drawingRequested, setDrawingRequested] = useState(false);
  useEffect(() => {
    if (showDrawing || showResultsModal) setDrawingRequested(true);
  }, [showDrawing, showResultsModal]);

  const { data: projectData } = useGetBallooningProjectQuery(
    { id: plan.project.id as string },
    { skip: !plan.project.id || !(showDrawing || showResultsModal || drawingRequested) }
  );

  useEffect(() => {
    if (projectData?.ballooning_pages) {
      const balloons = projectData.ballooning_pages
        .flatMap((page: any) => page.balloons || [])
        .filter((b: Balloon) => b.measurement_strategy === 'Keyence');
      setAllKeyenceBalloons(balloons);
    }
  }, [projectData]);

  // Get unique Keyence views for the filter
  const keyenceViews = useMemo(() => {
    const views = allKeyenceBalloons.map(b => b.view).filter(Boolean);
    return Array.from(new Set(views));
  }, [allKeyenceBalloons]);

  // Filter balloons by selected view
  const filteredBalloons = useMemo(() => {
    if (drawingViewFilter === 'all') return allKeyenceBalloons;
    return allKeyenceBalloons.filter(b => b.view === drawingViewFilter);
  }, [allKeyenceBalloons, drawingViewFilter]);

  // The results modal shows only the balloons of the report's own view.
  const resultsView = selectedSample ? parseSampleValue(selectedSample).view : null;
  const resultsBalloons = useMemo(
    () => allKeyenceBalloons.filter(b => b.view?.toLowerCase() === resultsView?.toLowerCase()),
    [allKeyenceBalloons, resultsView]
  );
  const activeResultFeature = useMemo(() => {
    const balloon = resultsBalloons.find(b => b.id === activeBalloonId);
    return balloon ? String(balloon.balloon_number) : null;
  }, [resultsBalloons, activeBalloonId]);

  // Open on a page that carries this view's balloons, once per opening, so
  // paging through the drawing afterwards is not snapped back.
  const [resultsPagePlaced, setResultsPagePlaced] = useState(false);
  useEffect(() => {
    if (!showResultsModal) {
      setResultsPagePlaced(false);
      return;
    }
    if (resultsPagePlaced || resultsBalloons.length === 0) return;
    if (!resultsBalloons.some(b => b.ballooning_page_number === pageNumber)) {
      setPageNumber(resultsBalloons[0].ballooning_page_number);
    }
    setResultsPagePlaced(true);
  }, [showResultsModal, resultsPagePlaced, resultsBalloons, pageNumber]);

  const resultsSource: KeyenceReportSource | null = useMemo(() => {
    if (!resultsReport || !selectedSample) return null;
    if (!resultsPreferLive && resultsReport.linked && resultsReport.id) {
      return { kind: 'stored', reportId: resultsReport.id };
    }
    const parsed = parseSampleValue(selectedSample);
    return {
      kind: 'live',
      jobProduct: plan.name,
      view: parsed.view,
      fileName: resultsReport.file_name,
      deviceName: parsed.deviceName || deviceName,
    };
  }, [resultsReport, resultsPreferLive, selectedSample, plan.name, deviceName]);

  const handleResultFeatureClick = (featureName: string) => {
    const balloon = resultsBalloons.find(b => String(b.balloon_number) === featureName);
    if (!balloon) return;
    if (balloon.ballooning_page_number) setPageNumber(balloon.ballooning_page_number);
    setActiveBalloonId(balloon.id);
    setCenterOnActive(true);
  };

  const currentPageID = useMemo(() => {
    const page = projectData?.ballooning_pages?.find((p: any) => p.page_number === pageNumber);
    return page?.id || null;
  }, [projectData, pageNumber]);

  const {
    data: planData,
    isLoading: isSamplesLoading,
    refetch: refetchSamples,
  } = useGetInspectionPlanByProjectQuery(
    { projectId: plan.project.id!, samples: 'ids', project_mode: 'id', device_name: deviceName },
    { skip: !plan.project.id }
  );

  // Disable inputs based on the status of the BATCH being inspected, not the shared
  // plan's owner OLI. On a Swiss-lot plan every batch shares one plan whose
  // project.order_line_item is the first batch, so keying off that locked every batch
  // once the first was signed off Completed (PTB-7023). Fall back to the owner before a
  // sample is selected (the per-sample controls are not shown yet anyway).
  const selectedBatchStatus = useMemo(() => {
    if (!selectedSample) return undefined;
    const { sampleId, view, deviceName: parsed } = parseSampleValue(selectedSample);
    const match = planData?.sample_view_list?.find(
      (entry: SampleViewItem) =>
        entry.sample_id === sampleId &&
        entry.view === view &&
        (entry.device_name || null) === (parsed || null)
    );
    return match?.batch_quality_status ?? undefined;
  }, [selectedSample, planData]);
  const lineItemStatus = selectedBatchStatus ?? plan?.project?.order_line_item?.status;
  const { controlsDisabled } = useControlsDisabled(lineItemStatus);

  // The sample number the operator writes on the bag. The selected value is an
  // id/view/device triple, so resolve it back to the sample it belongs to.
  const selectedSampleNumber = useMemo(() => {
    if (!selectedSample) return undefined;
    const { sampleId, view, deviceName: parsed } = parseSampleValue(selectedSample);
    const match = planData?.sample_view_list?.find(
      (entry: SampleViewItem) =>
        entry.sample_id === sampleId &&
        entry.view === view &&
        (entry.device_name || null) === (parsed || null)
    );
    return match?.sample_number ?? passedSample?.sample_number;
  }, [selectedSample, planData, passedSample]);

  // The Live queue hands us a plan from a different endpoint, so fall back to
  // the prop when the by-project payload has not arrived yet.
  const requiresSampleLabeling = Boolean(
    planData?.requires_inspection_sample_labeling ??
    plan?.requires_inspection_sample_labeling
  );

  const {
    data: keyenceReports = [],
    isLoading: isDependentLoading,
    isError: isDependentError,
    refetch: refetchKeyenceReports,
  } = useGetRelatedFilesQuery(
    selectedSample
      ? (() => {
        const { view, deviceName } = parseSampleValue(selectedSample);
        return {
          jobProduct: `${plan.name}`,
          view: view.toUpperCase(),
          device_name: deviceName,
          // Report Details browses every device CSV (including the linked one);
          // the Keyence queue still hides files already stored so Link only
          // offers unlinked reports.
          include_existing: showResults || undefined,
        };
      })()
      : undefined as any,
    { skip: !selectedSample }
  );

  const sortedReports = useMemo(() => {
    return [...keyenceReports].sort((a, b) => dayjs(a.measurement_time).valueOf() - dayjs(b.measurement_time).valueOf());
  }, [keyenceReports]);

  const [linkReport] = useLinkKeyenceReportMutation();
  const [overrideReport] = useOverrideKeyenceReportMutation();
  const [unlinkReport] = useUnlinkKeyenceReportMutation();
  const [deleteReport] = useDeleteKeyenceReportMutation();

  const handleDeleteReport = async (report: any) => {
    if (!report?.file_name) {
      message.error('Cannot delete: missing file name');
      return;
    }
    if (!selectedSample) {
      message.error('Cannot delete: no sample selected');
      return;
    }
    setLoading(true);
    try {
      const { deviceName: parsedDeviceName } = parseSampleValue(selectedSample);
      await deleteReport({
        file_name: report.file_name,
        dir_name: report.job_product,
        device_name: parsedDeviceName || deviceName || ''
      }).unwrap();
      message.success('Report deleted');
      if (selectedReport?.file_name === report.file_name) {
        setSelectedReport(null);
      }
      await refetchKeyenceReports();
    } catch (e) {
      message.error('Failed to delete report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (planData?.sample_view_list.length) {
      const linkedSample = planData.sample_view_list.find((sample) => sample.report?.linked);
      if (!selectedSample && linkedSample) {
        handleFirstDropdownChange(linkedSample.value);
      }
      const all = planData.sample_view_list.every((sample) => sample.report?.linked);
      setAllLinked(all);
    }
  }, [planData]);

  useEffect(() => {
    if (selectedSample) {
      refetchKeyenceReports();
    }
  }, [selectedSample]);

  useEffect(() => {
    if (!passedSample || !planData?.sample_view_list?.length) {
      return;
    }

    const matchedSample = planData.sample_view_list.find(
      (entry) => entry.value === passedSample.value
    );

    if (lockSample) {
      setInternalSelected(passedSample.value);

      if (matchedSample?.report) {
        const { report } = refreshSelectedSampleReport(passedSample.value, planData);
        setSelectedReport(report);
        setIsReportLocked(true);
      } else if (passedSample.report) {
        setSelectedReport(passedSample.report);
        setIsReportLocked(true);
      } else if (passedSample.recent_report && passedSample.recent_report.length > 0) {
        setSelectedReport(passedSample.recent_report[0]);
        setIsReportLocked(false);
      } else {
        setSelectedReport(null);
        setIsReportLocked(false);
      }
    } else {
      if (!isManualSelection) {
        setInternalSelected(passedSample.value);

        if (passedSample.report) {
          setSelectedReport(passedSample.report);
          setIsReportLocked(true);
        } else if (passedSample.recent_report && passedSample.recent_report.length > 0) {
          setSelectedReport(passedSample.recent_report[0]);
          setIsReportLocked(false);
        }
      }
    }
  }, [passedSample, planData, lockSample, isManualSelection]);

  const handleFirstDropdownChange = async (value: string) => {
    setIsManualSelection(true);
    setInternalSelected(value);

    const { report, locked } = refreshSelectedSampleReport(value, planData);
    setSelectedReport(report);
    setIsReportLocked(locked);

    try {
      await refetchKeyenceReports();
    } catch (e) {
      console.warn('Failed to refetch Keyence reports');
    }
  };

  const showActionError = (err: any) => {
    const detail = err?.data?.detail || err?.error || err?.message;
    if (!detail) {
      message.error('Action failed');
      return;
    }

    const lines = String(detail).split('\n').filter(Boolean);
    Modal.error({
      title: 'Keyence Report Validation Error',
      width: 720,
      maskClosable: true,
      keyboard: true,
      closable: true,
      okText: 'Close',
      // PTB-6441: a static Modal.error() mounts outside the ConfigProvider
      // tree, so Tailwind's preflight strips the primary button's styles and
      // Close renders invisible until hovered. Dismissing is the only way out
      // of this dialog, so it cannot be the button nobody can find.
      footer: (_originNode, { OkBtn }) => (
        <ConfigProvider theme={THEME}>
          <OkBtn />
        </ConfigProvider>
      ),
      content: (
        <div>
          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
            {lines.map((line: string, idx: number) => {
              const isLast = idx === lines.length - 1;
              return (
                <li key={idx} style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start' }}>
                  {!isLast && (
                    <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8, marginTop: 2 }} />
                  )}
                  {isLast ? <strong>{line}</strong> : <span>{line}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      ),
    });
  };

  const refreshCardState = async () => {
    try {
      const newPlanData = await refetchSamples().unwrap();
      await refetchKeyenceReports();
      if (selectedSample) {
        const { report, locked } = refreshSelectedSampleReport(selectedSample, newPlanData);
        setSelectedReport(report);
        setIsReportLocked(locked);
      }
      setAllLinked(newPlanData.sample_view_list.every((sample) => sample.report?.linked));
    } catch {
      // The action itself already reported its outcome, and the mutation
      // invalidates its tags, so a failed refresh should stay quiet rather than
      // look like the link failed.
    }
  };

  // Reads the judgment off the payload instead of component state: after a
  // conflict the card is resynced to what the server holds, so the selection
  // that started the action is no longer the one in state.
  const linkOrOverride = async (payload: any) => {
    if (payload.judgment === 'OK') {
      await linkReport(payload).unwrap();
    } else {
      await overrideReport(payload).unwrap();
    }
  };

  const promptReplaceLinkedReport = (payload: any, conflict: any) => {
    const existing = conflict?.existing_report || {};

    Modal.confirm({
      title: conflict?.slot
        ? `${conflict.slot} already has a linked report`
        : 'This sample already has a linked report',
      width: 640,
      okText: 'Clear it and link this one',
      cancelText: 'Keep the linked report',
      okButtonProps: { danger: true },
      // Same static-modal problem as showActionError: without this the two
      // choices render invisible on the tablet, which would leave the operator
      // as stuck as the error dialog this replaces.
      footer: (_, { OkBtn, CancelBtn }) => (
        <ConfigProvider theme={THEME}>
          <CancelBtn />
          <OkBtn />
        </ConfigProvider>
      ),
      content: (
        <div>
          <div
            style={{
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <Text type="secondary" style={{ fontSize: 13 }}>
              Currently linked
            </Text>
            <div style={{ fontWeight: 600 }}>{existing.file_name}</div>
            <div style={{ color: existing.judgment === 'OK' ? '#52c41a' : '#ff4d4f' }}>
              {[existing.measurement_time, existing.judgment].filter(Boolean).join(' – ')}
            </div>
            {existing.linked_by && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                Linked by {existing.linked_by}
              </Text>
            )}
          </div>
          <div>
            Clearing it replaces its measurements with the ones from{' '}
            <strong>{payload.file_name}</strong>.
          </div>
        </div>
      ),
      onOk: async () => {
        setLoading(true);
        try {
          await linkOrOverride({ ...payload, force: true });
          message.success('Report replaced');
          await refreshCardState();
          if (onUpdate && isLiveQueue) {
            onUpdate();
          }
        } catch (err: any) {
          showActionError(err);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSubmit = async () => {
    // The button's loading state lands a tick too late to stop a fast tablet
    // double-tap, so the in-flight guard has to be synchronous.
    if (submittingRef.current) return;

    if (!operatorId) {
      setOperatorError(true);
      message.error('Please enter your PIN before proceeding.');
      return;
    } else {
      setOperatorError(false);
    }

    if (!selectedSample) return;

    const { sampleId, view, deviceName: parsedDeviceName } = parseSampleValue(selectedSample);

    const payload = {
      sample: sampleId,
      view,
      device_name: parsedDeviceName || deviceName,
      file_name: selectedReport.file_name,
      judgment: selectedReport.judgment,
      measurement_time: selectedReport.measurement_time,
      operator_id: operatorId,
    };

    // Claimed before the labeling prompt so a second tap cannot stack a second
    // dialog; the one path that returns without reaching the try/finally has to
    // hand it back itself.
    submittingRef.current = true;

    // Unlinking is the operator undoing work, so it is not gated -- only the two
    // branches below that accept a report ask about the bag (PTB-6619).
    const isUnlinking = !!selectedReport.linked;
    if (requiresSampleLabeling && !isUnlinking) {
      let labeled = false;
      try {
        labeled = await confirmSampleLabeling(selectedSampleNumber);
      } catch {
        // A prompt that rejects has to hand the guard back too. It used to
        // leave submittingRef latched, so every later tap on Link/Unlink was
        // silently ignored until the card remounted -- a dead end on a tablet.
        submittingRef.current = false;
        message.error('Could not confirm sample labeling. Try again.');
        return;
      }
      if (!labeled) {
        submittingRef.current = false;
        message.warning('Bag and label the sample, then link the report.');
        return;
      }
    }

    setLoading(true);
    try {
      if (selectedReport.linked) {
        await unlinkReport(payload).unwrap();
        message.success('Report unlinked');
      } else {
        await linkOrOverride(payload);
        message.success(payload.judgment === 'OK' ? 'Report linked' : 'Report overridden');
      }
      await refreshCardState();

      if (onUpdate && isLiveQueue) {
        onUpdate();
      }
    } catch (err: any) {
      if (err?.status === 409 && err?.data?.code === 'report_already_linked') {
        // The card offered Link for a slot the server considers taken, usually
        // after a re-measure. Resync first so the button reflects what is
        // really linked, then let the operator clear it instead of leaving them
        // to retry an action that can never succeed.
        await refreshCardState();
        promptReplaceLinkedReport(payload, err.data);
      } else {
        // master's inline Modal.error moved into showActionError, which carries
        // the same themed footer.
        showActionError(err);
      }
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const handleComplete = () => {
    message.success('Keyence quality status marked as complete');
    if (onUpdate) {
      setTimeout(() => onUpdate(), 300);
    }
  };

  const isLinkDisabled = !selectedReport;
  const isFail = selectedReport?.judgment === 'Fail';
  const isOk = selectedReport?.judgment === 'OK';
  const isLinked = selectedReport?.linked;
  const buttonText = isLinked ? 'Unlink' : !selectedReport ? 'Link' : isOk ? 'Link' : 'Override';

  const linkedCount = planData?.sample_view_list?.filter((s) => s.report?.linked).length || 0;
  const totalSamples = planData?.sample_view_list?.length || 0;

  // After a link, the trigger stays openable so operators can browse device
  // CSVs and open Results — but rows are not selectable, so linking stays on
  // Unlink / the currently locked report.
  const reportsForPicker = useMemo(() => {
    if (
      !isReportLocked ||
      !selectedReport?.file_name ||
      sortedReports.some((r) => r.file_name === selectedReport.file_name)
    ) {
      return sortedReports;
    }
    return [selectedReport, ...sortedReports];
  }, [isReportLocked, selectedReport, sortedReports]);

  // Report Details lists CSVs other samples already hold (include_existing):
  // still shown, with Results, but never offered for Link.
  const linkedElsewhere = (r: any) =>
    r.linked_sample_number != null && r.linked_sample_number !== selectedSampleNumber;

  const reportOptions: TabletSelectOption[] = reportsForPicker.map((r) => ({
    value: r.file_name,
    disabled: isReportLocked || linkedElsewhere(r),
    label: (
      <span style={{ color: r.judgment === 'OK' ? '#52c41a' : '#ff4d4f' }}>
        {`${r.measurement_time} – ${r.judgment}`}
        {isReportLocked && r.file_name === selectedReport?.file_name ? ' (linked)' : ''}
        {linkedElsewhere(r) ? ` (linked to sample ${r.linked_sample_number})` : ''}
      </span>
    ),
    extra:
      r.judgment !== 'OK' || showResults ? (
        <Flex align="center" gap={8}>
          {r.judgment !== 'OK' && !isReportLocked && (
            <Popconfirm
              title="Delete this report?"
              description={`Delete ${r.file_name}?`}
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDeleteReport(r)}
              disabled={controlsDisabled}
            >
              <Button
                type="text"
                aria-label="Delete report"
                icon={<DeleteOutlined style={{ color: '#ff4d4f', fontSize: 22 }} />}
                style={{ width: 48, height: 48 }}
                disabled={controlsDisabled}
              />
            </Popconfirm>
          )}
          {showResults && (
            <Button
              icon={<TableOutlined aria-hidden />}
              aria-label={`Results for ${r.file_name}`}
              style={{ height: 48, borderRadius: 10, fontWeight: 600 }}
              onClick={() => openResults(r, true)}
            >
              Results
            </Button>
          )}
        </Flex>
      ) : undefined,
  }));

  const selectedSampleLabel = selectedSample
    ? planData?.sample_view_list?.find((s) => s.value === selectedSample)?.label
    : undefined;


  return (
    <>
      {/* Header bar */}
      <div
        style={
          framed
            ? { background: 'linear-gradient(135deg, #f8f9fb 0%, #eef1f5 100%)', padding: '12px 20px', borderBottom: '1px solid #f0f0f0' }
            : { paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }
        }
      >
        <Flex align="center" gap={14}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ margin: 0, fontWeight: 600, fontSize: 16, lineHeight: 1.3 }}>{plan.name}</Typography.Paragraph>
            <Flex gap={10} align="center" justify="center" style={{ marginTop: 2 }}>
              <Text strong style={{ fontSize: 13 }}>
                Due {dayjs(plan.project.order_line_item.due_date).format('MMM D, YYYY')}
              </Text>
              <Tag
                color={allLinked ? 'success' : linkedCount > 0 ? 'processing' : 'default'}
                icon={allLinked ? <CheckCircleFilled /> : <ClockCircleOutlined />}
                style={{ marginInlineEnd: 0 }}
              >
                {linkedCount}/{totalSamples} linked
              </Tag>
              {requiresSampleLabeling && (
                <InspectionSampleLabelingTag style={{ marginInlineEnd: 0 }} />
              )}
            </Flex>
          </div>
          <Flex gap={6}>
            <Tooltip title="View Keyence drawing">
              <Button
                type="primary"
                ghost
                icon={<FileImageOutlined style={{ fontSize: 24 }} />}
                style={{ width: 56, height: 56, borderRadius: 10 }}
                onClick={() => setShowDrawing(true)}
              />
            </Tooltip>
            <Tooltip title="Line item details">
              <Button
                icon={<InfoCircleOutlined style={{ fontSize: 24 }} />}
                style={{ width: 56, height: 56, borderRadius: 10 }}
                href={`/orders/line-items/${plan.project.order_line_item.id}`}
                target="_blank"
              />
            </Tooltip>
          </Flex>
        </Flex>
      </div>

      {/* Body */}
      <div style={{ padding: framed ? '16px 20px' : '16px 0 0' }}>
        <Flex gap={16} align="stretch">
          {/* Thumbnail */}
          <img
            src={plan.project.order_line_item.thumbnail_file}
            alt=""
            style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 10, border: '1px solid #e8e8e8', background: '#fafafa', flexShrink: 0, alignSelf: 'center' }}
          />

          {/* Selects */}
          <Flex vertical style={{ flex: 1, minWidth: 200 }} gap={10}>
            <TabletSelectModal
              title="Select Sample"
              placeholder="Select Sample"
              value={internalSelected}
              loading={isSamplesLoading}
              disabled={isSampleLocked || isSamplesLoading}
              emptyText="No samples"
              onChange={(value) => handleFirstDropdownChange(String(value))}
              options={(planData?.sample_view_list || []).map((sample) => ({
                value: sample.value,
                triggerLabel: sample.label,
                label: (
                  <span>
                    {sample.label}{' '}
                    {sample.report?.linked && (
                      <span style={{ color: '#52c41a', fontWeight: 600 }}> (linked)</span>
                    )}
                  </span>
                ),
              }))}
            />

            <TabletSelectModal
              title="Select Keyence Report"
              placeholder="Keyence Report"
              value={selectedReport?.file_name}
              disabled={!selectedSample || isDependentError}
              loading={isDependentLoading}
              emptyText="No reports"
              open={reportPickerOpen}
              onOpenChange={setReportPickerOpen}
              header={
                <div>
                  <Text strong ellipsis style={{ display: 'block', fontSize: 15 }}>
                    {plan.name}
                  </Text>
                  {selectedSampleLabel && (
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      {selectedSampleLabel}
                    </Text>
                  )}
                  {isReportLocked && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
                      Linking is complete — browse reports or view results; unlink first to change the linked CSV.
                    </Text>
                  )}
                </div>
              }
              onChange={(value) => {
                if (isReportLocked) return;
                const report = keyenceReports.find((r) => r.file_name === value);
                setSelectedReport(report || null);
              }}
              options={reportOptions}
            />

            <OperatorPinField
              userData={users || []}
              expectedGroups={['Keyence Operators']}
              value={operatorId}
              disabled={controlsDisabled}
              showError={operatorError}
              autoFocus={false}
              onResolved={(userId) => {
                setOperatorId(userId);
                if (userId) {
                  setOperatorError(false);
                }
              }}
            />
          </Flex>

          {/* Action buttons */}
          <Flex vertical gap={10} style={{ width: 120, flexShrink: 0 }} justify="flex-end">
            {showResults && (
              <Button
                size="large"
                icon={<TableOutlined aria-hidden />}
                disabled={!selectedReport?.file_name}
                style={{ height: 50, width: '100%', borderRadius: 10, fontSize: 16, fontWeight: 600, marginBottom: 'auto' }}
                onClick={() => openResults(selectedReport)}
              >
                Results
              </Button>
            )}
            <Button
              size="large"
              loading={loading}
              disabled={controlsDisabled || isLinkDisabled || isUserMissing}
              style={{
                height: 50, width: '100%', borderRadius: 10, fontSize: 16, fontWeight: 600,
                backgroundColor: isLinkDisabled ? '#e8e8e8' : isLinked ? '#fa8c16' : isOk ? '#52c41a' : '#ff4d4f',
                borderColor: 'transparent',
                color: isLinkDisabled ? '#999' : '#fff',
              }}
              onClick={handleSubmit}
            >
              {buttonText}
            </Button>

            {allLinked && (
              <Button
                size="large"
                disabled={controlsDisabled || plan.keyence_status === 'Completed' || isUserMissing}
                style={{
                  height: 50, width: '100%', borderRadius: 10, fontSize: 16, fontWeight: 600,
                  backgroundColor: plan.keyence_status === 'Completed' ? '#52c41a' : '#1890ff',
                  borderColor: 'transparent', color: '#fff',
                }}
                onClick={handleComplete}
              >
                {plan.keyence_status === 'Completed' ? '✓ Done' : 'Complete'}
              </Button>
            )}
          </Flex>
        </Flex>
      </div>

      {/* Measurement results modal, laid out like the drawing modal */}
      <Modal
        title={
          <Flex align="center" gap={16}>
            <span>{plan.name} — Keyence Results</span>
            {resultsView && <Tag color="blue">{resultsView}</Tag>}
          </Flex>
        }
        open={showResultsModal}
        onCancel={closeResults}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        // Above Report Details (and its nested CSV list if still animating closed).
        zIndex={2000}
        destroyOnClose
      >
        <div style={{ width: '100%', height: '85vh' }}>
          <Splitter layout="vertical" style={{ height: '100%' }}>
            <Splitter.Panel defaultSize="55%" min="20%">
              {currentPageID && projectData ? (
                <BallooningViewer
                  activeBalloonId={activeBalloonId}
                  setActiveBalloonId={setActiveBalloonId}
                  setCenterOnActive={setCenterOnActive}
                  centerOnActive={centerOnActive}
                  localBalloons={resultsBalloons}
                  setLocalBalloons={() => undefined}
                  balloonSize={projectData.balloon_size || 50}
                  editable={false}
                  pageID={currentPageID}
                  pageNumber={pageNumber}
                  setPageNumber={setPageNumber}
                  isTabletView={true}
                  projectId={plan.project.id}
                  fitMode="auto"
                  showLeaderLines
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Spin size="large" />
                </div>
              )}
            </Splitter.Panel>
            <Splitter.Panel min="20%">
              <div style={{ height: '100%', overflowY: 'auto', padding: '8px 0' }}>
                {resultsSource && (
                  <KeyenceSampleReportResults
                    source={resultsSource}
                    activeFeature={activeResultFeature}
                    onFeatureClick={handleResultFeatureClick}
                  />
                )}
              </div>
            </Splitter.Panel>
          </Splitter>
        </div>
      </Modal>

      {/* Drawing modal with view filter */}
      <Modal
        title={
          <Flex align="center" gap={16}>
            <span>{plan.name} — Keyence Drawing</span>
            <Radio.Group
              value={drawingViewFilter}
              onChange={(e) => setDrawingViewFilter(e.target.value)}
              size="large"
              buttonStyle="solid"
            >
              <Radio.Button value="all">All</Radio.Button>
              {keyenceViews.map(view => (
                <Radio.Button key={view} value={view}>{view}</Radio.Button>
              ))}
            </Radio.Group>
          </Flex>
        }
        open={showDrawing}
        onCancel={() => setShowDrawing(false)}
        footer={null}
        width="90vw"
        style={{ top: 20 }}
        destroyOnClose
      >
        <div style={{ width: '100%', height: '85vh' }}>
          <Splitter layout="vertical" style={{ height: '100%' }}>
            <Splitter.Panel defaultSize="65%" min="30%">
              {currentPageID && projectData ? (
                <BallooningViewer
                  activeBalloonId={activeBalloonId}
                  setActiveBalloonId={setActiveBalloonId}
                  setCenterOnActive={setCenterOnActive}
                  centerOnActive={centerOnActive}
                  localBalloons={filteredBalloons}
                  setLocalBalloons={() => {}}
                  balloonSize={projectData.balloon_size || 50}
                  editable={false}
                  pageID={currentPageID}
                  pageNumber={pageNumber}
                  setPageNumber={setPageNumber}
                  isTabletView={true}
                  projectId={plan.project.id}
                  fitMode="auto"
                  showLeaderLines
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Spin size="large" />
                </div>
              )}
            </Splitter.Panel>
            <Splitter.Panel min="20%">
              <div style={{ height: '100%', overflowY: 'auto', padding: '8px 0' }}>
                <Table
                  dataSource={filteredBalloons}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  onRow={(record) => ({
                    onClick: () => {
                      setActiveBalloonId(record.id);
                      setCenterOnActive(true);
                    },
                    style: { cursor: 'pointer' },
                  })}
                  columns={[
                    {
                      title: '#',
                      dataIndex: 'balloon_number',
                      key: 'balloon_number',
                      width: 60,
                      render: (num: number) => (
                        <div style={{
                          borderRadius: '50%', border: '2px solid #1890ff', width: 28, height: 28,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#1890ff', fontWeight: 600, fontSize: 13,
                        }}>
                          {num}
                        </div>
                      ),
                    },
                    {
                      title: 'View',
                      dataIndex: 'view',
                      key: 'view',
                      width: 100,
                      render: (v: string) => <Tag color="blue">{v}</Tag>,
                    },
                    {
                      title: 'Nominal',
                      dataIndex: 'nominal',
                      key: 'nominal',
                      width: 100,
                      render: (v: string) => trimZeros(v),
                    },
                    {
                      title: 'Plus',
                      dataIndex: 'tolerance_plus',
                      key: 'tolerance_plus',
                      width: 80,
                      render: (v: string) => v ? `+${trimZeros(v)}` : '—',
                    },
                    {
                      title: 'Minus',
                      dataIndex: 'tolerance_minus',
                      key: 'tolerance_minus',
                      width: 80,
                      render: (v: string) => trimZeros(v),
                    },
                    {
                      title: 'LCL',
                      key: 'lcl',
                      width: 90,
                      render: (_: any, record: any) => trimZeros(record.lower_control_limit),
                    },
                    {
                      title: 'UCL',
                      key: 'ucl',
                      width: 90,
                      render: (_: any, record: any) => trimZeros(record.upper_control_limit),
                    },
                  ]}
                />
              </div>
            </Splitter.Panel>
          </Splitter>
        </div>
      </Modal>
    </>
  );
};

const KeyenceCard = (props: KeyenceCardProps) => (
  <Card
    style={{ width: '95%', maxWidth: 1300, margin: '0 auto', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
    styles={{ body: { padding: 0 } }}
  >
    <KeyenceCardContent {...props} framed />
  </Card>
);

export default KeyenceCard;
